import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { RealEstateType } from '../../@types/realEstateType'
import { Lead, LeadSource } from '../../@types/lead'
import { Intent } from '../../@types/intent'
import { TimeHorizon } from '../../@types/timeHorizon'
import { Ownership } from '../../@types/ownership'
import { sendLead } from './thunks'
import { LandDevelopmentPotential } from '../../@types/landDevelopmentPotential'
import { LandDevelopmentStatus } from '../../@types/landDevelopmentStatus'
import { LandLayout } from '../../@types/landLayout'
import { CommercialRealEstateType } from '../../@types/commercialRealEstateType'
import { ApartmentSubtype, HouseSubtype } from '../../@types/realEstateSubtype'
import { Furnishing } from '../../@types/FurnishingType'
import { SolarIntent } from '../../@types/solarIntent'
import { PlacementType } from '../../@types/placementType'

export const defaultAreaValues = {
  [RealEstateType.HOUSE]: {
    livingSpace: 180,
    plotArea: 800,
  },
  [RealEstateType.APARTMENT]: {
    livingSpace: 180,
    plotArea: undefined,
  },
  [RealEstateType.LAND]: {
    livingSpace: undefined,
    plotArea: 1200,
  },
  [RealEstateType.COMMERCIAL]: {
    livingSpace: 200,
    plotArea: undefined,
  },
}

export const initialLeadState: Lead = {
  leadSource: LeadSource.REGULAR,
}

const leadSlice = createSlice({
  name: 'lead',
  initialState: initialLeadState,
  reducers: {
    updateRealEstateType: (state, action: PayloadAction<RealEstateType>) => {
      state.realEstateType = action.payload
    },
    updateOptionalStreet: (state, action: PayloadAction<string>) => {
      state.optionalStreet = action.payload
    },
    updateOptionalHouseNumber: (state, action: PayloadAction<string>) => {
      state.optionalHouseNumber = action.payload
    },
    updateLivingSpace: (state, action: PayloadAction<number>) => {
      state.livingSpace = action.payload
    },
    updatePlotArea: (state, action: PayloadAction<number | undefined>) => {
      state.plotArea = action.payload
    },
    updateConstructionYear: (state, action: PayloadAction<number | undefined>) => {
      state.constructionYear = action.payload
    },
    updateRoomsNumber: (state, action: PayloadAction<number>) => {
      state.rooms = action.payload
    },
    updateIntent: (state, action: PayloadAction<Intent>) => {
      state.intent = action.payload
    },
    updateSolarIntent: (state, action: PayloadAction<SolarIntent>) => {
      state.solarIntent = action.payload
    },
    updateExtendedBuyerLead: (state, action: PayloadAction<boolean>) => {
      state.extendedBuyerLead = action.payload
    },
    updateConsultationAccepted: (state, action: PayloadAction<boolean>) => {
      state.isConsultationAccepted = action.payload
    },
    updateTimeHorizon: (state, action: PayloadAction<TimeHorizon>) => {
      state.timeHorizon = action.payload
    },
    updateOwnership: (state, action: PayloadAction<Ownership>) => {
      state.ownership = action.payload
    },
    updateLandDevelopmentPotential: (state, action: PayloadAction<LandDevelopmentPotential>) => {
      state.landDevelopmentPotential = action.payload
    },
    updateLandDevelopmentStatus: (state, action: PayloadAction<LandDevelopmentStatus>) => {
      state.landDevelopmentStatus = action.payload
    },
    updateLandLayout: (state, action: PayloadAction<LandLayout>) => {
      state.landLayout = action.payload
    },
    updateCommercialRealEstateType: (state, action: PayloadAction<CommercialRealEstateType>) => {
      state.commercialRealEstateType = action.payload
    },
    updateRealEstateSubtype: (state, action: PayloadAction<ApartmentSubtype | HouseSubtype>) => {
      state.realEstateSubtype = action.payload
    },
    updateFurnishingType: (state, action: PayloadAction<Furnishing>) => {
      state.furnishingType = action.payload
    },
    updateAreaWithDefaultValues: (state) => {
      if (state.realEstateType) {
        state.livingSpace = defaultAreaValues[state.realEstateType].livingSpace
        state.plotArea = defaultAreaValues[state.realEstateType].plotArea
      }
    },
    updateLeadSource: (state, action: PayloadAction<LeadSource>) => {
      state.leadSource = action.payload
    },
    updateEmployment: (state, action: PayloadAction<string>) => {
      state.employment = action.payload
    },
    updateDefaultEmployment: (state, action: PayloadAction<string>) => {
      state.employment = action.payload
    },
    updateAffordability: (state, action: PayloadAction<number>) => {
      state.affordability = action.payload
    },
    updateEquity: (state, action: PayloadAction<number>) => {
      state.equity = action.payload
    },
    updateIncome: (state, action: PayloadAction<number>) => {
      state.income = action.payload
    },
    updatePlacementType: (state, action: PayloadAction<PlacementType>) => {
      state.placementType = action.payload
    },
    updateTextInputArea: (state, action: PayloadAction<string>) => {
      state.furtherRealEstateInformation = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendLead.fulfilled, (state, action) => {
      const { ssoId, rawLeadId } = action.payload
      return { ...state, id: rawLeadId, ssoId: ssoId, created: Date.now() }
    })
  },
})

export const {
  updateLivingSpace,
  updatePlotArea,
  updateRealEstateType,
  updateOptionalStreet,
  updateOptionalHouseNumber,
  updateRoomsNumber,
  updateIntent,
  updateSolarIntent,
  updateExtendedBuyerLead,
  updateTimeHorizon,
  updateOwnership,
  updateLandDevelopmentPotential,
  updateLandDevelopmentStatus,
  updateLandLayout,
  updateCommercialRealEstateType,
  updateConstructionYear,
  updateRealEstateSubtype,
  updateFurnishingType,
  updateAreaWithDefaultValues,
  updateLeadSource,
  updateEmployment,
  updateDefaultEmployment,
  updateAffordability,
  updateEquity,
  updateIncome,
  updatePlacementType,
  updateConsultationAccepted,
  updateTextInputArea,
} = leadSlice.actions
export default leadSlice.reducer
